/** biome-ignore-all lint/a11y/noNoninteractiveElementInteractions: <explanation> */
import cn from 'classnames';
import { useEffect, useRef } from 'react';
import { Switch } from 'sonata-react-components';

import { getClosestItems } from '@/helpers/common/getClosestItems';
import { COLOR_PICKER_COLORS } from './colorPicker.constants';
import './colorPicker.scss';

const COLORS = COLOR_PICKER_COLORS;

export type ColorPickerMode = 'compact' | 'half-line' | 'expanded-line' | 'full';
type Props = {
  className?: string;
  clickOutsideMode?: ColorPickerMode;
  label?: string;
  mode: ColorPickerMode;
  onModeChange?: (newMode: ColorPickerMode) => void;
  selected?: string;
  onColorChange?: (color: string) => void;
};

export function ColorPicker({
  className,
  clickOutsideMode = 'compact',
  label,
  mode = 'half-line',
  onModeChange,
  selected,
  onColorChange
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedIdx = selected ? COLORS.findIndex(color => color === selected) : -1;
  const closestColors = getClosestItems(COLORS, selectedIdx, 5);

  useEffect(() => {
    if (!onModeChange || mode === 'full') {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onModeChange(clickOutsideMode);
      }
    };
    if (mode === 'expanded-line') {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [clickOutsideMode, onModeChange, mode]);

  const handleColorSelect = (colorId: string) => {
    if (mode !== 'full') {
      onModeChange?.(clickOutsideMode);
    }
    onColorChange?.(colorId);
  };

  const isExpanded = ['expanded-line', 'full'].includes(mode);

  const handleContainerClick = () => {
    if (!isExpanded && onModeChange) {
      onModeChange('expanded-line');
    }
  };

  const selectedColorArray = selectedIdx !== -1 ? COLORS[selectedIdx] : COLORS[0];

  const getColorGradient = (color: string): string => color;

  return (
    <div className={cn('colorPicker', className)} ref={containerRef}>
      {label && (
        <div className="colorPicker__header">
          <div className="colorPicker__label">{label}</div>
        </div>
      )}
      <div
        className={cn('colorPicker__pickerContainer', {
          'colorPicker__pickerContainer--compact': mode === 'compact',
          'colorPicker__pickerContainer--expandedLine': mode === 'expanded-line',
          'colorPicker__pickerContainer--full': mode === 'full',
          'colorPicker__pickerContainer--halfLine': mode === 'half-line'
        })}
        onClick={handleContainerClick}
      >
        <Switch>
          <Switch.Case condition={mode === 'compact'}>
            <div className="colorPicker__collapsed">
              <div
                className={cn('colorPicker__circle', 'colorPicker__circle--selected')}
                style={{
                  background: getColorGradient(COLORS[selectedIdx])
                }}
              />
            </div>
          </Switch.Case>
          <Switch.Case condition={mode === 'half-line'}>
            <div className="colorPicker__halfLine">
              {closestColors.map((color, i) => (
                <div
                  className={cn('colorPicker__circle', {
                    'colorPicker__circle--selected': color === selectedColorArray
                  })}
                  key={i}
                  style={{
                    background: getColorGradient(color)
                  }}
                />
              ))}
            </div>
          </Switch.Case>
          <Switch.Default>
            <div className="colorPicker__expanded">
              {COLORS.map((color, idx) => (
                <div
                  className={cn('colorButtonWrapper', {
                    'colorButtonWrapper--selected': color === selected
                  })}
                  key={idx}
                  onClick={() => handleColorSelect(color)}
                  style={{
                    backgroundColor: color
                  }}
                />
              ))}
            </div>
          </Switch.Default>
        </Switch>
      </div>
    </div>
  );
}
